import React, { useState, useRef, useEffect, useCallback } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { Button } from "primereact/button";
import "primereact/resources/themes/lara-light-indigo/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import "./DraggableTable.scss";

/**
 * Componente de tabla con drag & drop
 * @param {Object} props
 * @param {Array} props.data - Array de datos a mostrar
 * @param {Array} props.columns - Columnas de la tabla
 * @param {Function} props.onReorder - Callback cuando se reordena (recibe array reordenado)
 * @param {Boolean} props.loading - Estado de carga
 * @param {String} props.emptyMessage - Mensaje cuando no hay datos
 * @param {Boolean} props.showActions - Mostrar columna de acciones
 * @param {Function} props.onEdit - Callback para editar
 * @param {Function} props.onShow - Callback para editar
 * @param {Function} props.onDelete - Callback para eliminar
 * @param {Function} props.onRowClick - Callback cuando se hace click en una fila
 * @param {Boolean} props.striped - Filas alternadas
 * @param {Boolean} props.hoverable - Efecto hover
 * @param {String} props.idField - Campo que contiene el ID único (default: 'id')
 * @param {Object} props.pagination - Objeto de paginación {total, total_pages, current_page, prev_page, next_page, per_page}
 * @param {Function} props.onPageChange - Callback cuando cambia la página (recibe page y rowsPerPage)
 * @param {Array} props.rowsPerPageOptions - Opciones para filas por página
 * @param {Boolean} props.showStatusFilter - Mostrar filtros de estado (Todo, Activos, Inactivos)
 * @param {Function} props.onStatusFilterChange - Callback cuando cambia el filtro de estado (recibe 'all', 'activo', 'inactivo')
 * @param {String} props.currentStatusFilter - Filtro de estado actual ('all', 'activo', 'inactivo')
 */
const DraggableTable = ({
    data = [],
    columns = [],
    onReorder,
    loading = false,
    emptyMessage = "No hay datos disponibles",
    showActions = false,
    onEdit = null,
    onShow = null,
    onDelete = null,
    onRowClick = null,
    striped = true,
    hoverable = true,
    className = "",
    idField = "id",
    actionColumnWidth = "150px",
    editTooltip = "Editar",
    deleteTooltip = "Eliminar",
    pagination = null,
    onPageChange = null,
    rowsPerPageOptions = [10, 25, 50, 100],
    showStatusFilter = false,
    onStatusFilterChange = null,
    currentStatusFilter = "all",
}) => {
    const [isDragging, setIsDragging] = useState(false);
    const headerRef = useRef(null);
    const bodyRef = useRef(null);

    // Callback para combinar las refs
    const setBodyRef = useCallback((node) => {
        bodyRef.current = node;
    }, []);

    // Sincronizar scroll del header con el body
    useEffect(() => {
        const bodyElement = bodyRef.current;
        const headerElement = headerRef.current;

        if (!bodyElement || !headerElement) return;

        const handleScroll = () => {
            if (headerElement) {
                headerElement.scrollLeft = bodyElement.scrollLeft;
            }
        };

        bodyElement.addEventListener("scroll", handleScroll);
        return () => bodyElement.removeEventListener("scroll", handleScroll);
    }, []);

    const handleDragEnd = (result) => {
        setIsDragging(false);

        if (!result.destination) {
            return;
        }

        if (result.source.index === result.destination.index) {
            return;
        }

        const items = Array.from(data);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);

        // Llamar al callback con los datos reordenados
        if (onReorder) {
            onReorder(items);
        }
    };

    const handleDragStart = () => {
        setIsDragging(true);
    };

    // Template para las acciones
    const actionsBodyTemplate = (rowData) => {
        return (
            <div className="draggable-table__actions">
                {onShow && (
                    <Button
                        icon="pi pi-eye"
                        className="draggable-table__actions-button draggable-table__actions-button--show"
                        onClick={(e) => {
                            e.stopPropagation();
                            onShow(rowData);
                        }}
                        tooltip="Ver detalles"
                        tooltipOptions={{ position: "top" }}
                    />
                )}
                {onEdit && (
                    <Button
                        icon="pi pi-pencil"
                        className="draggable-table__actions-button draggable-table__actions-button--edit"
                        onClick={(e) => {
                            e.stopPropagation();
                            onEdit(rowData);
                        }}
                        tooltip={editTooltip}
                        tooltipOptions={{ position: "top" }}
                    />
                )}
                {onDelete && (
                    <Button
                        icon="pi pi-trash"
                        className="draggable-table__actions-button draggable-table__actions-button--delete"
                        onClick={(e) => {
                            e.stopPropagation();
                            onDelete(rowData);
                        }}
                        tooltip={deleteTooltip}
                        tooltipOptions={{ position: "top" }}
                    />
                )}
            </div>
        );
    };

    // Template para el drag handle
    const dragHandleTemplate = () => {
        return (
            <div className="draggable-table__drag-handle">
                <i className="pi pi-bars"></i>
            </div>
        );
    };

    // Calcular gridTemplateColumns
    const gridTemplateColumns = [
        "50px",
        ...columns.map((col) => col.width || "1fr"),
        showActions ? actionColumnWidth : "",
    ]
        .filter(Boolean)
        .join(" ");

    // Renderizar celdas del header
    const renderHeaderCells = () => {
        return columns.map((col, index) => (
            <div
                key={index}
                className="draggable-table__header-cell"
                style={col.style}
            >
                {col.header}
            </div>
        ));
    };

    // Renderizar celda de acciones en header
    const renderHeaderActions = () => {
        if (!showActions) return null;
        return (
            <div className="draggable-table__header-cell draggable-table__header-cell--actions">
                Acciones
            </div>
        );
    };

    // Template personalizado para cada fila
    const rowTemplate = (rowData, index) => {
        return (
            <Draggable
                key={rowData[idField]}
                draggableId={String(rowData[idField])}
                index={index}
            >
                {(provided, snapshot) => {
                    // Combinar estilos de grid con estilos de dragging
                    const combinedStyle = {
                        gridTemplateColumns,
                        ...provided.draggableProps.style,
                    };

                    return (
                        <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            className={`
                                draggable-table__row
                                ${
                                    snapshot.isDragging
                                        ? "draggable-table__row--dragging"
                                        : ""
                                }
                                ${
                                    onRowClick
                                        ? "draggable-table__row--clickable"
                                        : ""
                                }
                            `}
                            style={combinedStyle}
                            onClick={() =>
                                !snapshot.isDragging &&
                                onRowClick &&
                                onRowClick(rowData)
                            }
                        >
                            {/* Drag handle */}
                            <div
                                {...provided.dragHandleProps}
                                className="draggable-table__cell draggable-table__cell--drag"
                            >
                                {dragHandleTemplate()}
                            </div>

                            {/* Data columns */}
                            {columns.map((col, colIndex) => {
                                const cellValue = col.body
                                    ? col.body(rowData)
                                    : rowData[col.field];
                                const isString = typeof cellValue === "string";
                                const shouldShowTitle =
                                    isString && cellValue.length > 30;

                                return (
                                    <div
                                        key={colIndex}
                                        className="draggable-table__cell"
                                        style={col.style}
                                        title={
                                            shouldShowTitle
                                                ? cellValue
                                                : undefined
                                        }
                                    >
                                        {cellValue}
                                    </div>
                                );
                            })}

                            {/* Actions column */}
                            {showActions && (
                                <div className="draggable-table__cell draggable-table__cell--actions">
                                    {actionsBodyTemplate(rowData)}
                                </div>
                            )}
                        </div>
                    );
                }}
            </Draggable>
        );
    };

    return (
        <div className={`draggable-table ${className}`}>
            {/* Filtros de estado */}
            {showStatusFilter && onStatusFilterChange && (
                <div className="draggable-table__status-filter">
                    <button
                        className={`draggable-table__status-btn ${
                            currentStatusFilter === "all"
                                ? "draggable-table__status-btn--active"
                                : ""
                        }`}
                        onClick={() => onStatusFilterChange("all")}
                    >
                        <i className="pi pi-list"></i>
                        <span>Todo</span>
                    </button>
                    <button
                        className={`draggable-table__status-btn draggable-table__status-btn--activo ${
                            currentStatusFilter === "activo"
                                ? "draggable-table__status-btn--active"
                                : ""
                        }`}
                        onClick={() => onStatusFilterChange("activo")}
                    >
                        <i className="pi pi-check-circle"></i>
                        <span>Activos</span>
                    </button>
                    <button
                        className={`draggable-table__status-btn draggable-table__status-btn--inactivo ${
                            currentStatusFilter === "inactivo"
                                ? "draggable-table__status-btn--active"
                                : ""
                        }`}
                        onClick={() => onStatusFilterChange("inactivo")}
                    >
                        <i className="pi pi-times-circle"></i>
                        <span>Inactivos</span>
                    </button>
                </div>
            )}

            {/* Contador de registros */}
            {data.length > 0 && (
                <div className="draggable-table__total">
                    {data.length} {data.length === 1 ? "registro" : "registros"}
                    {isDragging && (
                        <span className="draggable-table__drag-indicator">
                            <i className="pi pi-arrows-alt"></i>
                            Reorganizando...
                        </span>
                    )}
                </div>
            )}

            <span style={{ display: "none" }}>{isDragging}</span>

            <DragDropContext
                onDragEnd={handleDragEnd}
                onDragStart={handleDragStart}
            >
                <div className="draggable-table__container">
                    {/* Header */}
                    <div className="draggable-table__header">
                        <div
                            ref={headerRef}
                            className="draggable-table__header-wrapper"
                        >
                            <div
                                className="draggable-table__header-row"
                                style={{ gridTemplateColumns }}
                            >
                                <div className="draggable-table__header-cell draggable-table__header-cell--drag">
                                    <i className="pi pi-sort"></i>
                                </div>
                                {renderHeaderCells()}
                                {renderHeaderActions()}
                            </div>
                        </div>
                    </div>

                    {/* Body with Droppable */}
                    {!loading && (
                        <Droppable droppableId="table-body">
                            {(provided, snapshot) => {
                                // Función para combinar las refs
                                const combineRefs = (el) => {
                                    provided.innerRef(el);
                                    setBodyRef(el);
                                };

                                const bodyClassName = snapshot.isDraggingOver
                                    ? "draggable-table__body draggable-table__body--dragging-over"
                                    : "draggable-table__body";

                                return (
                                    <div
                                        ref={combineRefs}
                                        {...provided.droppableProps}
                                        className={bodyClassName}
                                    >
                                        {data.length === 0 ? (
                                            <div className="draggable-table__empty">
                                                {emptyMessage}
                                            </div>
                                        ) : (
                                            data.map((rowData, index) =>
                                                rowTemplate(rowData, index),
                                            )
                                        )}
                                        {provided.placeholder}
                                    </div>
                                );
                            }}
                        </Droppable>
                    )}
                    {loading && (
                        <div className="draggable-table__body">
                            <div className="draggable-table__loading">
                                <i className="pi pi-spin pi-spinner"></i>
                                <span>Cargando...</span>
                            </div>
                        </div>
                    )}
                </div>
            </DragDropContext>

            {/* Pagination */}
            {pagination && onPageChange && !loading && data.length > 0 && (
                <div className="draggable-table__pagination">
                    <div className="draggable-table__pagination-info">
                        Mostrando{" "}
                        {(pagination.current_page - 1) * pagination.per_page +
                            1}{" "}
                        a{" "}
                        {Math.min(
                            pagination.current_page * pagination.per_page,
                            pagination.total,
                        )}{" "}
                        de {pagination.total} registros
                    </div>

                    <div className="draggable-table__pagination-controls">
                        {/* Rows per page selector */}
                        <div className="draggable-table__pagination-rows">
                            <label>Filas por página:</label>
                            <select
                                value={pagination.per_page}
                                onChange={(e) =>
                                    onPageChange(1, parseInt(e.target.value))
                                }
                                className="draggable-table__pagination-select"
                            >
                                {rowsPerPageOptions.map((option) => (
                                    <option key={option} value={option}>
                                        {option}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Page navigation */}
                        <div className="draggable-table__pagination-nav">
                            <Button
                                icon="pi pi-angle-double-left"
                                onClick={() =>
                                    onPageChange(1, pagination.per_page)
                                }
                                disabled={pagination.current_page === 1}
                                className="draggable-table__pagination-button"
                                tooltip="Primera página"
                            />
                            <Button
                                icon="pi pi-angle-left"
                                onClick={() =>
                                    onPageChange(
                                        pagination.current_page - 1,
                                        pagination.per_page,
                                    )
                                }
                                disabled={!pagination.prev_page}
                                className="draggable-table__pagination-button"
                                tooltip="Página anterior"
                            />

                            <span className="draggable-table__pagination-page">
                                Página {pagination.current_page} de{" "}
                                {pagination.total_pages}
                            </span>

                            <Button
                                icon="pi pi-angle-right"
                                onClick={() =>
                                    onPageChange(
                                        pagination.current_page + 1,
                                        pagination.per_page,
                                    )
                                }
                                disabled={!pagination.next_page}
                                className="draggable-table__pagination-button"
                                tooltip="Página siguiente"
                            />
                            <Button
                                icon="pi pi-angle-double-right"
                                onClick={() =>
                                    onPageChange(
                                        pagination.total_pages,
                                        pagination.per_page,
                                    )
                                }
                                disabled={
                                    pagination.current_page ===
                                    pagination.total_pages
                                }
                                className="draggable-table__pagination-button"
                                tooltip="Última página"
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DraggableTable;
